
import {
    getBaseDate,
    isPrevMonth,
    isPrevYear,
    isThisMonth,
    isThisYear,
    isToday,
    isYesterday
} from "data/dateUtils";

import { getMeasurementCodesByQuery } from "components/axios/specAxios";

/**
 * 초 단위 누적값을 분 단위로 변환한다.
 * 각 행을 변환하지 않고, 항목별로 초를 먼저 합산한 후 호출한다.
 */
function secondsToMinutes(seconds) {
    return Math.round((seconds / 60) * 100) / 100;
}

/**
 * 항목별 운전시간 집계
 *
 * 반환값:
 * [
 *   {
 *     key: "FORWARD",
 *     name: "정방향",
 *     prevData: [0, 10, ...],
 *     currData: [0, 20, ...],
 *     prevTotal: 10,
 *     currTotal: 20
 *   }
 * ]
 *
 * prevData / currData는 분 단위다.
 */
export async function buildDetailDurationGroups(
    data,
    axis,
    period,
    viewType
) {
    const rows = Array.isArray(data) ? data : [];

    const fieldMap = {
        DAY: "hour",
        MONTH: "day",
        YEAR: "month"
    };

    const targetMap = {
        DAY: {
            prev: isYesterday,
            curr: isToday
        },
        MONTH: {
            prev: isPrevMonth,
            curr: isThisMonth
        },
        YEAR: {
            prev: isPrevYear,
            curr: isThisYear
        }
    };

    const field = fieldMap[period];
    const targets = targetMap[period];

    if (!field || !targets) {
        return [];
    }

    const axisKeys = axis.map(String);
    const groups = new Map();

    rows.forEach((item) => {
        const rawKey =
            viewType === "OPERATION"
                ? item.value
                : item.equipmentPartId;

        if (rawKey === undefined || rawKey === null || rawKey === "") {
            return;
        }

        const key = String(rawKey);

        if (!groups.has(key)) {
            groups.set(key, {
                key,
                rawName:
                    viewType === "OPERATION"
                        ? key
                        : String(item.equipmentPartName || key),

                // 초 단위로 누적한다.
                prevSeconds: Array(axisKeys.length).fill(0),
                currSeconds: Array(axisKeys.length).fill(0)
            });
        }

        const axisValue =
            field === "hour"
                ? String(item[field]).padStart(2, "0")
                : String(item[field]);

        const index = axisKeys.indexOf(axisValue);

        if (index === -1) {
            return;
        }

        const seconds = Math.max(
            0,
            Number(item.durationSum) || 0
        );

        const group = groups.get(key);

        if (targets.prev(item)) {
            group.prevSeconds[index] += seconds;
        }

        if (targets.curr(item)) {
            group.currSeconds[index] += seconds;
        }
    });

    const groupList = Array.from(groups.values());

    // 운전 코드의 표시명 조회
    if (viewType === "OPERATION") {
        await Promise.all(
            groupList.map(async (group) => {
                try {
                    const result = await getMeasurementCodesByQuery({
                        code: group.key
                    });

                    group.rawName =
                        result?._embedded?.measurementCodes?.[0]?.name ||
                        group.key;
                } catch (error) {
                    // 코드명 조회에 실패하면 코드값을 표시한다.
                    group.rawName = group.key;
                }
            })
        );
    }

    return groupList.map((group) => {
        // const prevData = group.prevSeconds.map(secondsToMinutes);
        // const currData = group.currSeconds.map(secondsToMinutes);

        // return {
        //     key: group.key,
        //     name: group.rawName,
        //     prevData,
        //     currData,
        //     prevTotal: secondsToMinutes(
        //         group.prevSeconds.reduce((sum, value) => sum + value, 0)
        //     ),
        //     currTotal: secondsToMinutes(
        //         group.currSeconds.reduce((sum, value) => sum + value, 0)
        //     )
        // };
        const convertSeconds = (seconds) =>
            period === "DAY"
                ? Math.round((seconds / 60) * 10) / 10
                : Math.round((seconds / 3600) * 100) / 100;

            const prevData = group.prevSeconds.map(convertSeconds);
            const currData = group.currSeconds.map(convertSeconds);

            return {
                key: group.key,
                name: group.rawName,
                prevData,
                currData,
                prevTotal: convertSeconds(
                    group.prevSeconds.reduce((sum, value) => sum + value, 0)
                ),
                currTotal: convertSeconds(
                    group.currSeconds.reduce((sum, value) => sum + value, 0)
                )
            };
    });
}

/**
 * 항목 그룹을 ECharts 시리즈로 변환한다.
 *
 * 전체 항목 선택:
 *   현재 기간 시리즈만 반환
 *
 * 특정 항목 선택:
 *   이전 기간 + 현재 기간 시리즈 반환
 */
export function buildDetailDurationSeries(
    groups,
    period,
    selectedItemKey = null
) {
    const currentLabel = getBaseDate(period, "CUR").value;
    const previousLabel = getBaseDate(period, "PREV").value;

    const selectedGroups = selectedItemKey === null
        ? groups
        : groups.filter((group) => group.key === selectedItemKey);

    if (selectedItemKey === null) {
        return selectedGroups.map((group) => ({
            id: String(group.key),
            name: group.name,
            data: group.currData
        }));
    }

    return selectedGroups.flatMap((group) => [
        {
            id: `${group.key}_PREV`,
            name: `${group.name} (${previousLabel})`,
            data: group.prevData
        },
        {
            id: `${group.key}_CURR`,
            name: `${group.name} (${currentLabel})`,
            data: group.currData
        }
    ]);
}
